import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1, 0),
    width: "100%"
  }
}));

const EditCardDialog = ({ open, onClose, card }) => {
  const classes = useStyles();
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    tags: "",
    dueDate: null
  });

  // Determinar o idioma atual para o DatePicker
  const locale = i18n.language === "pt" ? ptBR : enUS;

  useEffect(() => {
    if (card) {
      setFormData({
        title: card.title || "",
        description: card.description || "",
        priority: card.priority || "medium",
        tags: card.tags || "",
        dueDate: card.dueDate ? new Date(card.dueDate) : null
      });
    }
  }, [card, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, dueDate: date }));
  };

  const handleSave = async () => {
    try {
      if (!formData.title) {
        toast.error(i18n.t("Title is required"));
        return;
      }

      await api.put(`/kanban/cards/${card.id}`, formData);
      
      toast.success(i18n.t("ticketCard.updated"));
      
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Erro ao atualizar cartão");
    }
  };

  if (!card) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="edit-card-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="edit-card-dialog-title">
        {i18n.t("kanban.actions.editCard")}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          name="title"
          label={i18n.t("kanban.fields.title")}
          value={formData.title}
          onChange={handleChange}
          fullWidth
          margin="dense"
          variant="outlined"
          className={classes.formControl}
        />
        
        <TextField
          name="description"
          label={i18n.t("kanban.fields.description")}
          value={formData.description}
          onChange={handleChange}
          fullWidth
          multiline
          minRows={3}
          margin="dense"
          variant="outlined"
          className={classes.formControl}
        />
        
        <FormControl 
          variant="outlined" 
          className={classes.formControl}
          margin="dense"
        >
          <InputLabel id="priority-label">
            {i18n.t("kanban.fields.priority")}
          </InputLabel>
          <Select
            labelId="priority-label"
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            label={i18n.t("kanban.fields.priority")}
          >
            <MenuItem value="low">{i18n.t("kanban.priorities.low")}</MenuItem>
            <MenuItem value="medium">{i18n.t("kanban.priorities.medium")}</MenuItem>
            <MenuItem value="high">{i18n.t("kanban.priorities.high")}</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          name="tags"
          label={i18n.t("kanban.fields.tags")}
          value={formData.tags}
          onChange={handleChange}
          fullWidth
          margin="dense"
          variant="outlined"
          className={classes.formControl}
          placeholder="tag1, tag2, tag3"
          helperText={i18n.t("kanban.helperTexts.separeteCommas")}
        />
        
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
          <DatePicker
            disableToolbar
            variant="inline"
            inputVariant="outlined"
            format="dd/MM/yyyy"
            margin="normal"
            label={i18n.t("kanban.fields.dueDate")}
            value={formData.dueDate}
            onChange={handleDateChange}
            fullWidth
            className={classes.formControl}
            clearable="true"
            autoOk
          />
        </MuiPickersUtilsProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          {i18n.t("kanban.buttons.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary">
          {i18n.t("kanban.buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCardDialog;